import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useWalkthroughContext } from './WalkthroughProvider';
import { X, ChevronLeft, ChevronRight, SkipForward, Lightbulb, AlertCircle, Link2, Target } from 'lucide-react';

function getElementRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top + window.scrollY, left: r.left + window.scrollX, width: r.width, height: r.height, viewTop: r.top, viewLeft: r.left };
}

function scrollToElement(selector) {
  if (!selector) return;
  const el = document.querySelector(selector);
  if (!el) return;
  const r = el.getBoundingClientRect();
  if (r.top < 80 || r.bottom > window.innerHeight - 100) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

export function WalkthroughOverlay() {
  const ctx = useWalkthroughContext();
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, arrowDir: 'top' });
  const tooltipRef = useRef(null);

  const { activeTour, currentStep, nextStep, prevStep, skipTour, completeTour, userRole } = ctx || {};

  const step = activeTour?.steps?.[currentStep];
  const totalSteps = activeTour?.steps?.length || 0;
  const isLast = currentStep === totalSteps - 1;

  useEffect(() => {
    if (!step) return;
    const update = () => {
      if (step.target) {
        scrollToElement(step.target);
        setTimeout(() => {
          const r = getElementRect(step.target);
          setRect(r);
        }, 400);
      } else {
        setRect(null);
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [step]);

  useEffect(() => {
    if (!tooltipRef.current) return;
    const tt = tooltipRef.current;
    const ttRect = tt.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = 16;

    if (!rect) {
      setTooltipPos({ top: vh / 2 - ttRect.height / 2, left: vw / 2 - ttRect.width / 2, arrowDir: 'none' });
      return;
    }

    const targetCenterX = rect.viewLeft + rect.width / 2;
    const targetCenterY = rect.viewTop + rect.height / 2;
    let top, left, arrowDir;

    // Try below
    if (rect.viewTop + rect.height + pad + ttRect.height < vh) {
      top = rect.viewTop + rect.height + pad;
      left = Math.max(pad, Math.min(targetCenterX - ttRect.width / 2, vw - ttRect.width - pad));
      arrowDir = 'top';
    }
    // Try above
    else if (rect.viewTop - pad - ttRect.height > 0) {
      top = rect.viewTop - ttRect.height - pad;
      left = Math.max(pad, Math.min(targetCenterX - ttRect.width / 2, vw - ttRect.width - pad));
      arrowDir = 'bottom';
    }
    // Try right
    else if (rect.viewLeft + rect.width + pad + ttRect.width < vw) {
      top = Math.max(pad, Math.min(targetCenterY - ttRect.height / 2, vh - ttRect.height - pad));
      left = rect.viewLeft + rect.width + pad;
      arrowDir = 'left';
    }
    // Try left
    else {
      top = Math.max(pad, Math.min(targetCenterY - ttRect.height / 2, vh - ttRect.height - pad));
      left = Math.max(pad, rect.viewLeft - ttRect.width - pad);
      arrowDir = 'right';
    }

    setTooltipPos({ top, left, arrowDir });
  }, [rect, step, currentStep]);

  if (!activeTour || !step) return null;

  // Filter step by role
  if (step.roles && !step.roles.includes(userRole) && !step.roles.includes('all')) return null;

  const roleContent = step.roleContent?.[userRole] || {};

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-testid="walkthrough-overlay">
      {/* Semi-transparent overlay with spotlight cutout */}
      <svg className="fixed inset-0 w-full h-full pointer-events-none" style={{ zIndex: 9999 }}>
        <defs>
          <mask id="wt-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.viewLeft - 6}
                y={rect.viewTop - 6}
                width={rect.width + 12}
                height={rect.height + 12}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(15,23,42,0.55)" mask="url(#wt-spotlight-mask)" />
      </svg>

      {/* Spotlight ring */}
      {rect && (
        <div
          className="fixed border-2 border-blue-400 rounded-lg pointer-events-none animate-pulse"
          style={{
            top: rect.viewTop - 6, left: rect.viewLeft - 6,
            width: rect.width + 12, height: rect.height + 12,
            zIndex: 10000, boxShadow: '0 0 0 4px rgba(59,130,246,0.2)',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="fixed w-[340px] sm:w-[400px] bg-white rounded-xl shadow-2xl border border-slate-200"
        style={{ top: tooltipPos.top, left: tooltipPos.left, zIndex: 10001, maxHeight: '80vh', overflow: 'auto' }}
        data-testid="walkthrough-tooltip"
      >
        {/* Progress bar */}
        <div className="h-1 bg-slate-100 rounded-t-xl overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600" data-testid="walkthrough-step-indicator">
            Step {currentStep + 1} of {totalSteps}
          </span>
          <button
            onClick={skipTour}
            className="text-slate-400 hover:text-slate-600 transition-colors p-1"
            data-testid="walkthrough-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-3">
          <h3 className="text-base font-bold text-slate-900 mb-2" data-testid="walkthrough-title">
            {roleContent.title || step.title}
          </h3>

          <p className="text-sm text-slate-600 mb-3">
            {roleContent.description || step.description}
          </p>

          {/* Info sections */}
          <div className="space-y-2 mb-3">
            {(roleContent.whatIsThis || step.whatIsThis) && (
              <div className="flex gap-2 bg-blue-50 rounded-lg p-2.5">
                <Target className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-bold uppercase text-blue-600 mb-0.5">What this is</p>
                  <p className="text-xs text-blue-800">{roleContent.whatIsThis || step.whatIsThis}</p>
                </div>
              </div>
            )}
            {(roleContent.whyItMatters || step.whyItMatters) && (
              <div className="flex gap-2 bg-amber-50 rounded-lg p-2.5">
                <Lightbulb className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-bold uppercase text-amber-600 mb-0.5">Why it matters</p>
                  <p className="text-xs text-amber-800">{roleContent.whyItMatters || step.whyItMatters}</p>
                </div>
              </div>
            )}
            {(roleContent.whatToDo || step.whatToDo) && (
              <div className="flex gap-2 bg-green-50 rounded-lg p-2.5">
                <AlertCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-bold uppercase text-green-600 mb-0.5">What to do</p>
                  <p className="text-xs text-green-800">{roleContent.whatToDo || step.whatToDo}</p>
                </div>
              </div>
            )}
            {step.example && (
              <div className="flex gap-2 bg-slate-50 rounded-lg p-2.5">
                <Link2 className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Example</p>
                  <p className="text-xs text-slate-700 italic">{step.example}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between px-4 pb-4 pt-1 border-t border-slate-100">
          <button
            onClick={skipTour}
            className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors"
            data-testid="walkthrough-skip-btn"
          >
            <SkipForward className="h-3 w-3" />
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={prevStep}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                data-testid="walkthrough-prev-btn"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </button>
            )}
            <button
              onClick={isLast ? completeTour : nextStep}
              className="flex items-center gap-1 px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              data-testid="walkthrough-next-btn"
            >
              {isLast ? 'Finish' : 'Next'}
              {!isLast && <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
